import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { GameState } from '../../../engine';
import { holeCounter, worldFor } from '../../brand';
import { holeLabel } from '../../game/viewModel';
import { Lockup, WorldMedallion } from '../Art';
import { IconClose, IconMenu, IconMute, IconScores, IconSound } from '../Icons';

/**
 * menu · world medallion + "3/9" · game code (online) · scorecard · sound · help.
 * A guided lesson (game/guide.ts) passes `center` (shown instead of the hole
 * chip) and `minimal` (no scorecard or help; the menu button becomes a close).
 */
export function TopBar({
  state,
  muted,
  onMenu,
  onScores,
  onMute,
  onHelp,
  roomCode,
  center,
  minimal = false,
}: {
  state: GameState;
  muted: boolean;
  onMenu: () => void;
  onScores: () => void;
  onMute: () => void;
  onHelp: () => void;
  roomCode?: string;
  center?: ReactNode;
  minimal?: boolean;
}) {
  const world = worldFor(state.hole, state.totalHoles);
  const [tip, setTip] = useState(false);
  const t = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(t.current), []);
  const showTip = () => {
    setTip(true);
    clearTimeout(t.current);
    t.current = setTimeout(() => setTip(false), 2000);
  };
  return (
    <header className="topbar">
      {minimal ? (
        <button className="icon-btn" onClick={onMenu} aria-label="Leave the tutorial">
          <IconClose />
        </button>
      ) : (
        <button className="icon-btn" onClick={onMenu} aria-label="Menu">
          <IconMenu />
        </button>
      )}
      <Lockup />
      <div className="topbar-mid">
        {center ?? (
          <button type="button" className="hole-chip" onClick={showTip} aria-label={`${holeLabel(state)}, ${world.name}`}>
            <WorldMedallion world={world} size={26} />
            <span className="num hole-count">{holeCounter(state.hole, state.totalHoles)}</span>
            {tip && (
              <span className="hole-tip mark" role="status">
                {world.name}
              </span>
            )}
          </button>
        )}
        {roomCode && (
          <span className="room-tag" title="Room code">
            {roomCode}
          </span>
        )}
      </div>
      {!minimal && (
        <button className="icon-btn" onClick={onScores} aria-label="Scorecard">
          <IconScores />
        </button>
      )}
      <button className="icon-btn" onClick={onMute} aria-label={muted ? 'Sound off. Turn sound on' : 'Sound on. Turn sound off'} aria-pressed={!muted}>
        {muted ? <IconMute /> : <IconSound />}
      </button>
      {!minimal && (
        <button className="icon-btn help-btn" onClick={onHelp} aria-label="Help">
          ?
        </button>
      )}
    </header>
  );
}
